import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  X,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  User,
} from "lucide-react";
import toast from "react-hot-toast";
import { getTeacherById } from "../../services/teachers";

interface LeaveRequestsModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacherId: string;
}

interface LeaveRequest {
  _id: string;
  startDate: string;
  endDate: string;
  reason: string;
  type: "sick" | "vacation" | "personal" | "emergency";
  status: "pending" | "approved" | "rejected";
  appliedDate: string;
  documents?: string[];
  adminNotes?: string;
}

const LeaveRequestsModal: React.FC<LeaveRequestsModalProps> = ({
  isOpen,
  onClose,
  teacherId,
}) => {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<
    "all" | "pending" | "approved" | "rejected"
  >("all");
  const [selectedRequest, setSelectedRequest] = useState<LeaveRequest | null>(
    null
  );
  const [adminNotes, setAdminNotes] = useState("");

  // Fetch teacher data
  const { data: teacher } = useQuery({
    queryKey: ["teacher", teacherId],
    queryFn: () => getTeacherById(teacherId),
    enabled: isOpen && !!teacherId,
  });

  // Fetch leave requests
  const { data: leaveRequestsResponse, isLoading } = useQuery({
    queryKey: ["leaveRequests", teacherId, filter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filter !== "all") params.append("status", filter);

      const API_URL =
        import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(
        `${API_URL}/api/teachers/${teacherId}/leave-requests?${params}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch leave requests");
      return response.json();
    },
    enabled: isOpen && !!teacherId,
  });

  // Approve/Reject leave request mutation
  const updateLeaveRequestMutation = useMutation({
    mutationFn: async (data: {
      requestId: string;
      status: "approved" | "rejected";
      notes: string;
    }) => {
      const API_URL =
        import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(
        `${API_URL}/api/leave-requests/${data.requestId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            status: data.status,
            adminNotes: data.notes,
          }),
        }
      );
      if (!response.ok) throw new Error("Failed to update leave request");
      return response.json();
    },
    onSuccess: (_, variables) => {
      toast.success(`Leave request ${variables.status} successfully`);
      queryClient.invalidateQueries({ queryKey: ["leaveRequests", teacherId] });
      setSelectedRequest(null);
      setAdminNotes("");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update leave request");
    },
  });

  const handleApprove = () => {
    if (selectedRequest) {
      updateLeaveRequestMutation.mutate({
        requestId: selectedRequest._id,
        status: "approved",
        notes: adminNotes,
      });
    }
  };

  const handleReject = () => {
    if (selectedRequest) {
      updateLeaveRequestMutation.mutate({
        requestId: selectedRequest._id,
        status: "rejected",
        notes: adminNotes,
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "sick":
        return "bg-red-100 text-red-800";
      case "vacation":
        return "bg-blue-100 text-blue-800";
      case "personal":
        return "bg-purple-100 text-purple-800";
      case "emergency":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (!isOpen) return null;

  const teacherData = teacher?.data as any;
  const leaveRequests: LeaveRequest[] = leaveRequestsResponse?.data || [];
  const filteredRequests =
    filter === "all"
      ? leaveRequests
      : leaveRequests.filter((req) => req.status === filter);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="h-6 w-6 text-purple-600" />
            Leave Requests - {teacherData?.userInfo?.firstName}{" "}
            {teacherData?.userInfo?.lastName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Filter Tabs */}
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-6">
            {["all", "pending", "approved", "rejected"].map((filterOption) => (
              <button
                key={filterOption}
                onClick={() => setFilter(filterOption as any)}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  filter === filterOption
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-600 hover:text-blue-600"
                }`}
              >
                {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
                {filterOption !== "all" && (
                  <span className="ml-2 bg-gray-200 px-2 py-1 rounded-full text-xs">
                    {
                      leaveRequests.filter((req) => req.status === filterOption)
                        .length
                    }
                  </span>
                )}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Leave Requests
              </h3>
              <p className="text-gray-500">
                {filter === "all"
                  ? "This teacher has not submitted any leave requests yet."
                  : `No ${filter} leave requests found.`}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRequests.map((request) => (
                <div
                  key={request._id}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getTypeColor(
                            request.type
                          )}`}
                        >
                          {request.type.charAt(0).toUpperCase() +
                            request.type.slice(1)}
                        </span>
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            request.status
                          )}`}
                        >
                          {request.status === "pending" && (
                            <Clock className="w-3 h-3 mr-1" />
                          )}
                          {request.status === "approved" && (
                            <CheckCircle className="w-3 h-3 mr-1" />
                          )}
                          {request.status === "rejected" && (
                            <XCircle className="w-3 h-3 mr-1" />
                          )}
                          {request.status.charAt(0).toUpperCase() +
                            request.status.slice(1)}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Date Range
                          </p>
                          <p className="text-sm text-gray-600">
                            {new Date(request.startDate).toLocaleDateString()} -{" "}
                            {new Date(request.endDate).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Applied On
                          </p>
                          <p className="text-sm text-gray-600">
                            {new Date(request.appliedDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-900 mb-1">
                          Reason
                        </p>
                        <p className="text-sm text-gray-600">
                          {request.reason}
                        </p>
                      </div>

                      {request.adminNotes && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            Admin Notes
                          </p>
                          <p className="text-sm text-gray-600">
                            {request.adminNotes}
                          </p>
                        </div>
                      )}
                    </div>

                    {request.status === "pending" && (
                      <div className="ml-4">
                        <button
                          onClick={() => setSelectedRequest(request)}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700"
                        >
                          Review
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Review Modal */}
        {selectedRequest && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-60">
            <div className="bg-white rounded-lg w-full max-w-md">
              <div className="flex justify-between items-center p-6 border-b">
                <h3 className="text-lg font-bold text-gray-900">
                  Review Leave Request
                </h3>
                <button
                  onClick={() => {
                    setSelectedRequest(null);
                    setAdminNotes("");
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">
                    Request Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="font-medium">Type:</span>{" "}
                      {selectedRequest.type}
                    </p>
                    <p>
                      <span className="font-medium">Duration:</span>{" "}
                      {new Date(selectedRequest.startDate).toLocaleDateString()}{" "}
                      - {new Date(selectedRequest.endDate).toLocaleDateString()}
                    </p>
                    <p>
                      <span className="font-medium">Reason:</span>{" "}
                      {selectedRequest.reason}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Admin Notes (Optional)
                  </label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add any notes about this decision..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleReject}
                    disabled={updateLeaveRequestMutation.isPending}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <XCircle className="h-4 w-4" />
                    Reject
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={updateLeaveRequestMutation.isPending}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Approve
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveRequestsModal;
